'use client';

import React from 'react';
import Link from 'next/link';
import { Icon } from "@iconify/react";
import { useLanguage } from '@/app/context/AppContext';

export default function Footer() {
  const { lang } = useLanguage();

  const labels = {
    privacyPolicy: lang === 'pt' ? 'Política de Privacidade' : 'Privacy Policy',
    terms: lang === 'pt' ? 'Termos de Serviço' : 'Terms of Service',
    cookiePolicy: lang === 'pt' ? 'Política de Cookies' : 'Cookie Policy',
    contact: lang === 'pt' ? 'Contacto' : 'Contact',
    company: lang === 'pt' ? 'Empresa' : 'Company',
    usefulLinks: lang === 'pt' ? 'Links Úteis' : 'Useful Links',
    ourStory: lang === 'pt' ? 'A Nossa História' : 'Our Story',
    meetTeam: lang === 'pt' ? 'A Nossa Equipa' : 'Meet the Team',
    services: lang === 'pt' ? 'Serviços' : 'Services',
    contactUs: lang === 'pt' ? 'Contacte-nos' : 'Contact Us',
    faq: lang === 'pt' ? 'Perguntas Frequentes' : 'FAQ',
    refund: lang === 'pt' ? 'Política de Reembolso' : 'Refund Policy',
    aml: lang === 'pt' ? 'Política AML' : 'AML Policy',
    connectWith: lang === 'pt' ? 'Siga-nos' : 'Connect with us',
    mobileApp: lang === 'pt' ? 'Aplicação Móvel' : 'Mobile App',
    copyright: lang === 'pt' ? 'Direitos reservados' : 'All rights reserved',
    phone: lang === 'pt' ? 'Telefone' : 'Phone',
    email: lang === 'pt' ? 'E-mail' : 'Email',
    rssJobFeed: lang === 'pt' ? 'RSS - Vagas de Emprego' : 'RSS - Job Vacancies',
  };

  return (
    <footer className="bg-white border-t border-gray-200 pt-10 pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header Section */}
        <div className="flex flex-col md:flex-row items-start md:items-center mb-10">
          <Link href="/">
            <img src="/logo.png" alt="Autônomo" className="h-12 w-auto mb-4 md:mb-0 mr-8 mix-blend-multiply" />
          </Link>
          <div className="text-sm text-gray-500 font-medium">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <Link href="/privacy" className="hover:text-teal-600 transition">{labels.privacyPolicy}</Link>
              <span className="text-gray-300">|</span>
              <Link href="/terms" className="hover:text-teal-600 transition">{labels.terms}</Link>
              <span className="text-gray-300">|</span>
              <Link href="/cookie-policy" className="hover:text-teal-600 transition">{labels.cookiePolicy}</Link>
            </div>
            <p className="text-gray-400">Copyright Autonomo Lda, {new Date().getFullYear()}</p>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-10">
          <div className="text-sm text-gray-600 space-y-2 font-medium">
            <p className="text-gray-800 font-bold mb-3">Autonomo Lda</p>
            <p>Angola, Luanda</p>
            {lang === 'pt' ? (
              <>
                <p>Rua Rainha Ginga, centro de Imagiologia 4 de Fevereiro (Mais conhecido por Sindicato), junto ao Hotel Costa</p>
              </>
            ) : (
              <p>Rua Rainha Ginga, centro de Imagiologia 4 de Fevereiro (Mais conhecido por Sindicato), junto ao Hotel Costa.</p>
            )}
            <p>{labels.email}: <a href="mailto:geral@autonomoangola.com" className="hover:text-teal-600 transition">geral@autonomoangola.com</a></p>
            <p>{labels.phone}: +244937219336 | +244956751585</p>
          </div>

          <div className="text-sm text-gray-600 space-y-2 font-medium">
            <p className="text-gray-800 font-bold mb-3">{labels.company}</p>
            <p><Link href="/about?tab=story" className="hover:text-teal-600 transition block">{labels.ourStory}</Link></p>
            <p><Link href="/about?tab=team" className="hover:text-teal-600 transition block">{labels.meetTeam}</Link></p>
            <p><Link href="/about?tab=services" className="hover:text-teal-600 transition block">{labels.services}</Link></p>
            <p><Link href="/about?tab=contact" className="hover:text-teal-600 transition block">{labels.contactUs}</Link></p>
          </div>

          <div className="text-sm text-gray-600 space-y-2 font-medium">
            <p className="text-gray-800 font-bold mb-3">{labels.usefulLinks}</p>
            <p><Link href="/faq" className="hover:text-teal-600 transition block">{labels.faq}</Link></p>
            <p><Link href="/refund" className="hover:text-teal-600 transition block">{labels.refund}</Link></p>
            <p><Link href="/aml-policy" className="hover:text-teal-600 transition block">{labels.aml}</Link></p>
            <p>
              <a
                href="/feed/jobs.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-orange-500 transition flex items-center gap-1.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-orange-500 flex-shrink-0" aria-hidden="true">
                  <path d="M6.18 15.64a2.18 2.18 0 0 1 2.18 2.18C8.36 19.01 7.38 20 6.18 20C4.98 20 4 19.01 4 17.82a2.18 2.18 0 0 1 2.18-2.18M4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44m0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93V10.1z" />
                </svg>
                {labels.rssJobFeed}
              </a>
            </p>
          </div>
        </div>

        {/* Bottom Connect Section */}
        <div className="border-t border-gray-200 pt-6 flex flex-col sm:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <span className="text-gray-700 font-semibold text-sm mr-2">{labels.connectWith}</span>
            <div className="flex items-center gap-3">
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-teal-600 transition">
                <Icon icon={"mdi:instagram"} />
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-teal-600 transition">
                <Icon icon={"ri:facebook-fill"} />
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-teal-600 transition">
                <Icon icon={"prime:twitter"} />
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-teal-600 transition">
                <Icon icon={"mdi:youtube"} />
              </a>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-gray-700 font-semibold text-sm mr-2">{labels.mobileApp}</span>
            <div className="flex items-center gap-3">
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-teal-600 transition">
                <Icon icon={"simple-icons:appstore"} />
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-teal-600 transition">
                <Icon icon={"mage:playstore"} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
